/**
 * The help center (docs/help/, published to the Axiss widget) from inside the app.
 *
 * Inside a Rumio site the site already shows its widget around our frame, so a
 * "Learn more" asks the host to open the article there (`rumio:help`, contract
 * v1.23; an older host drops it). On its own the app loads the widget itself,
 * with its bubble, when the server names a key (`help_widget_key`).
 */
import { useEffect, useState } from "react";
import { api } from "./api";
import { inIframe, sendHelpRequest } from "./iframe-bridge";
import type { HelpArticle } from "./help-slugs";

export const HELP_WIDGET_SRC = "https://widget.axiss.support/axiss-widget.js";

interface AxissApi {
  open: (view?: string) => void;
  openArticle?: (slug: string) => void;
}

declare global {
  interface Window {
    Axiss?: AxissApi;
  }
}

let keyRequest: Promise<string | null> | null = null;

/** The widget key, asked of the server once per page. */
export function helpWidgetKey(): Promise<string | null> {
  keyRequest ??= api
    .get<{ data: { help_widget_key?: string | null } }>("/api/standalone/config")
    .then((res) => res.data.help_widget_key ?? null)
    .catch(() => {
      keyRequest = null;
      return null;
    });
  return keyRequest;
}

let loading: Promise<AxissApi | null> | null = null;

/** Add the widget's script once; resolves when `window.Axiss` is there. */
export function loadHelpWidget(key: string): Promise<AxissApi | null> {
  if (window.Axiss) return Promise.resolve(window.Axiss);
  loading ??= new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = HELP_WIDGET_SRC;
    script.async = true;
    script.dataset.axissKey = key;
    script.onload = () => resolve(window.Axiss ?? null);
    script.onerror = () => {
      loading = null;
      script.remove();
      resolve(null);
    };
    document.body.appendChild(script);
  });
  return loading;
}

/** Open one article: in the host's widget when embedded, else in ours. */
export async function openHelp(article: HelpArticle): Promise<void> {
  if (inIframe()) {
    sendHelpRequest(article);
    return;
  }
  const key = await helpWidgetKey();
  if (!key) return;
  const axiss = await loadHelpWidget(key);
  if (!axiss) return;
  if (typeof axiss.openArticle === "function") axiss.openArticle(article);
  else axiss.open("helpcenter");
}

/** Whether "Learn more" can go anywhere: always inside a site, else when a key is set. */
export function useHelpAvailable(): boolean {
  const embedded = inIframe();
  const [available, setAvailable] = useState(embedded);
  useEffect(() => {
    if (embedded) return;
    let live = true;
    void helpWidgetKey().then((key) => live && setAvailable(Boolean(key)));
    return () => {
      live = false;
    };
  }, [embedded]);
  return available;
}

/** For tests: forget the cached key and script. */
export function resetHelpForTests(): void {
  keyRequest = null;
  loading = null;
}
